import React, { useState } from "react";

interface Item {
  id: string;
  youSaid: string;
  weDid: string;
  source: string;
  category: string;
  date: string;
  status: string;
  doneDate: string;
}

const KEY = "you_said_we_did";

function load(): Item[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

export default function YouSaidWeDid({ user }: { user?: any }) {
  const [items, setItems] = useState<Item[]>(load);
  const [form, setForm] = useState<Partial<Item>>({ status: "Listening", date: new Date().toISOString().slice(0, 10), source: "Resident" });
  const [message, setMessage] = useState("");

  const save = (next: Item[]) => {
    setItems(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const add = () => {
    if (!form.youSaid) {
      setMessage("Enter what was said first.");
      return;
    }
    save([{
      id: `yswd-${Date.now()}`,
      youSaid: form.youSaid || "",
      weDid: form.weDid || "",
      source: form.source || "Resident",
      category: form.category || "General",
      date: form.date || new Date().toISOString().slice(0, 10),
      status: form.status || "Listening",
      doneDate: "",
    }, ...items]);
    setForm({ status: "Listening", date: new Date().toISOString().slice(0, 10), source: "Resident" });
    setMessage("Added to the board.");
  };

  const update = (id: string, patch: Partial<Item>) => {
    save(items.map(i => (i.id === id ? { ...i, ...patch, doneDate: patch.status === "Done" && !i.doneDate ? new Date().toISOString().slice(0, 10) : i.doneDate } : i)));
  };

  const remove = (id: string) => save(items.filter(i => i.id !== id));

  const importFeedback = () => {
    let fb: any[] = [];
    try {
      fb = JSON.parse(localStorage.getItem("feedback_records") || "[]");
    } catch {
      fb = [];
    }
    const existing = new Set(items.map(i => i.youSaid));
    const added = fb.filter(f => f.comment && !existing.has(String(f.comment))).map(f => ({
      id: `yswd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      youSaid: String(f.comment),
      weDid: "",
      source: f.submitter || "Family",
      category: f.type || "Feedback",
      date: f.date || new Date().toISOString().slice(0, 10),
      status: "Listening",
      doneDate: "",
    }));
    if (added.length === 0) {
      setMessage("No new feedback to import.");
      return;
    }
    save([...added, ...items]);
    setMessage(`Imported ${added.length} feedback item(s) onto the board.`);
  };

  const done = items.filter(i => i.status === "Done").length;
  const inProgress = items.filter(i => i.status === "In Progress").length;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">You Said, We Did</h2>
        <p className="text-sm text-slate-600">The visible proof that resident, family and staff feedback becomes real change. Share this board in resident meetings.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm"><div className="text-2xl font-bold text-slate-900">{items.length}</div><div className="text-xs text-slate-500">On the board</div></div>
        <div className="rounded-xl border border-blue-300 bg-blue-50 p-4 text-center"><div className="text-2xl font-bold text-blue-700">{inProgress}</div><div className="text-xs text-blue-700">In progress</div></div>
        <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-center"><div className="text-2xl font-bold text-green-700">{done}</div><div className="text-xs text-green-700">Done - we did it</div></div>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Add to the board</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">You said (the feedback)</label>
            <textarea value={form.youSaid || ""} onChange={e => setForm(f => ({ ...f, youSaid: e.target.value }))} rows={2} placeholder="e.g. Residents asked for more garden time in the afternoons" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">We did (the change made)</label>
            <textarea value={form.weDid || ""} onChange={e => setForm(f => ({ ...f, weDid: e.target.value }))} rows={2} placeholder="e.g. We now run a daily 3pm garden club with activities" className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>
          <div className="grid grid-cols-3 gap-2 md:col-span-2">
            <select value={form.source || "Resident"} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option>Resident</option><option>Family</option><option>Staff</option><option>Visitor</option>
            </select>
            <select value={form.category || "General"} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option>General</option><option>Activities</option><option>Food</option><option>Environment</option><option>Care</option>
            </select>
            <select value={form.status || "Listening"} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option>Listening</option><option>In Progress</option><option>Done</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={add} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Add to board</button>
          <button onClick={importFeedback} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Import from Feedback module</button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map(i => (
          <div key={i.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-slate-500">{i.source} | {i.category} | {i.date}</div>
              <div className="flex items-center gap-2">
                <select value={i.status} onChange={e => update(i.id, { status: e.target.value })} className={`rounded-lg border px-2 py-1 text-xs font-semibold ${i.status === "Done" ? "border-green-300 bg-green-50 text-green-700" : i.status === "In Progress" ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-300 bg-slate-50 text-slate-600"}`}>
                  <option>Listening</option><option>In Progress</option><option>Done</option>
                </select>
                <button onClick={() => remove(i.id)} className="text-xs text-red-600">Remove</button>
              </div>
            </div>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              <div className="rounded-lg bg-amber-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">You said</div>
                <div className="mt-1 text-sm text-slate-800">{i.youSaid}</div>
              </div>
              <div className="rounded-lg bg-green-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-green-700">We did{i.doneDate ? ` (completed ${i.doneDate})` : ""}</div>
                <div className="mt-1 text-sm text-slate-800">{i.weDid || "Action in progress - update when complete."}</div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">Nothing on the board yet. Add feedback or import it from the Feedback module.</div> : null}
      </div>
    </div>
  );
}